import { useCallback, useEffect, useRef, useState } from 'react';

export type EstadoDeCarga = 'cargando' | 'listo' | 'error';

/**
 * Pide algo al servidor y dice en qué está: cargando, listo o error, con
 * `recargar` para el «Reintentar». Si las dependencias cambian mientras una
 * respuesta viene en camino, esa respuesta vieja se descarta: la lista nunca
 * muestra la página 1 cuando ya se pidió la 2.
 */
export function useCarga<T>(pedir: () => Promise<T>, dependencias: unknown[]) {
  const [datos, setDatos] = useState<T | null>(null);
  const [estado, setEstado] = useState<EstadoDeCarga>('cargando');
  const [error, setError] = useState<string | null>(null);
  const turno = useRef(0);
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;

  const cargar = useCallback(async () => {
    const mio = ++turno.current;
    setEstado('cargando');
    setError(null);
    try {
      const respuesta = await pedirRef.current();
      if (mio !== turno.current) return;
      setDatos(respuesta);
      setEstado('listo');
    } catch (e) {
      if (mio !== turno.current) return;
      setError(e instanceof Error ? e.message : 'No pudimos cargar los datos.');
      setEstado('error');
    }
  }, []);

  useEffect(() => {
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencias);

  return { datos, estado, error, recargar: cargar };
}
